import { SubmitOrderItem } from "@amzn/ito-client";
import {
    CatalogItem,
    CATALOG_ITEM_UNIQUE_IDENTIFIER,
    getUseStandardOrderOutput,
    useStandardOrderOutput,
    useStandardOrderProps,
} from "interfaces";
import { useEffect, useState } from "react";

/**
 * Represent the minimum number of orders before disabling the remove button
 */
export const MINIMUM_NUMBER_OF_ORDERS: number = 1;

/**
 * Constant representing the default quantity allowed
 */
const DEFAULT_QUANTITY: number = 0;

/**
 * Constant representing the minimum quantity allowed per order
 */
const MINIMUM_QUANTITY_ALLOWED: number = 1;

/**
 * Formats a standard order map into the structure required by ITO client
 *
 * @param {Map<CatalogItem, number>} standardOrder - Map of catalog items and their quantities
 * @return {Record<string, SubmitOrderItem>} - Formatted order object with taxonomy IDs as keys
 */
export const formatStandardOrderForITO = (standardOrder: Map<CatalogItem, number>): Record<string, SubmitOrderItem> => {
    let mappedStandardOrder: Record<string, SubmitOrderItem> = {};

    standardOrder.forEach((quantity, item) => {
        mappedStandardOrder[item.taxonomyId] = {
            quantity: quantity,
        };
    });

    return mappedStandardOrder;
};

/**
 * Custom hook that returns the required parameters to manage the useStandardOrder hook
 *
 * @param {Map<CatalogItem, number> | null} items - The items received as initial values for the orders
 * @return {getUseStandardOrderOutput} - An object containing the standard items and a function to update them
 */
export const getUseStandardOrder = (items: Map<CatalogItem, number> | null): getUseStandardOrderOutput => {
    const [standardOrder, setStandardOrder] = useState<Map<CatalogItem, number>>(new Map());

    useEffect(() => {
        if (items == null) return;

        setStandardOrder(items);
    }, [items]);

    return {
        standardOrder,
        setStandardOrder,
    };
};

/**
 * Custom hook for managing the standard order confirmation
 *
 * @param {Map<CatalogItem, number>} standardOrder - The standard orders
 * @param {React.Dispatch<React.SetStateAction<Map<CatalogItem, number>>>} setStandardOrder - Function to update the standard orders
 * @return {useStandardOrderOutput} - An object containing the standard order catalog and auxiliary functions
 */
const useStandardOrder = ({ standardOrder, setStandardOrder }: useStandardOrderProps): useStandardOrderOutput => {
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);

    const trackBy: string = CATALOG_ITEM_UNIQUE_IDENTIFIER;

    useEffect(() => {
        setCatalog(Array.from(standardOrder.keys()));
    }, [standardOrder]);

    /**
     * Function that returns the selected quantity of a hardware order
     *
     * @param {CatalogItem} item - The item to retrieve the quantity
     * @return {number} - The quantity selected for the hardware
     */
    const getQuantity = (item: CatalogItem): number => standardOrder.get(item) || DEFAULT_QUANTITY;

    /**
     * Function that ensures a quantity value is not below the minimum allowed
     *
     * @param {number} quantity - The quantity value to validate
     * @return {number} - The validated quantity, either the input value or minimum allowed
     */
    const enforceMinimumQuantity = (quantity: number): number =>
        quantity < MINIMUM_QUANTITY_ALLOWED ? MINIMUM_QUANTITY_ALLOWED : quantity;

    /**
     * Function that updates the quantity of a selected hardware order
     *
     * @param {CatalogItem} item - The item to update the quantity
     * @param {number} value - The new quantity selected
     * @return {void} - No return value
     */
    const updateQuantity = (item: CatalogItem, value: number): void => {
        const enforcedQuantity: number = enforceMinimumQuantity(value);

        setStandardOrder((prev) => new Map(prev).set(item, enforcedQuantity));
    };

    /**
     * Function that removes an item from the map
     *
     * @param {CatalogItem} item - The item to remove from the map
     * @return {void} - No return value
     */
    const removeItem = (item: CatalogItem): void => {
        if (!canRemoveItems()) return;

        setStandardOrder((prev) => {
            const newQuantities = new Map(prev);
            newQuantities.delete(item);
            return newQuantities;
        });
    };

    /**
     * Function that returns if items can be removed
     *
     * @return {boolean} - Flag representing if items can be removed
     */
    const canRemoveItems = (): boolean => catalog.length > MINIMUM_NUMBER_OF_ORDERS;

    /**
     * Function that determines if the current quantity selected is a valid quantity
     *
     * @param {number} quantity - The quantity to validate
     * @return {boolean} - True if quantity is valid, false otherwise
     */
    const isValidQuantity = (quantity: number) => quantity >= MINIMUM_NUMBER_OF_ORDERS;

    return {
        catalog,
        trackBy,
        getQuantity,
        updateQuantity,
        removeItem,
        canRemoveItems,
        isValidQuantity,
    };
};

export default useStandardOrder;
